import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, ExternalLink, Inbox, Trash2, CheckSquare, Square } from "lucide-react";
import * as adminService from "../../services/adminService";
import { useToast } from "../../contexts/ToastContext";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { useAdmin } from "../../hooks/useAdmin";

const STATUS_OPTIONS = [
  { id: "all", label: "All" },
  { id: "open", label: "Open" },
  { id: "under_review", label: "Under Review" },
  { id: "resolved", label: "Resolved" },
  { id: "rejected", label: "Rejected" },
];

const ENTITY_OPTIONS = [
  { id: "all", label: "All entities" },
  { id: "profile", label: "Profiles" },
  { id: "product", label: "Products" },
];

const STATUS_STYLE = {
  open: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300",
  under_review: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
  resolved: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300",
  rejected: "bg-gray-100 text-gray-700 dark:bg-neutral-800 dark:text-gray-300",
};

function formatTime(ts) {
  if (!ts) return "";
  const date = ts.seconds ? new Date(ts.seconds * 1000) : new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
}

function formatReason(value) {
  const reason = String(value || "other").replace(/_/g, " ");
  return reason.charAt(0).toUpperCase() + reason.slice(1);
}

function ReportCard({ report, onChanged, isSuperAdmin, onDelete, adminUser }) {
  const toast = useToast();
  const [expanded, setExpanded] = useState(false);
  const [adminNote, setAdminNote] = useState(report.adminNote || "");
  const [dialog, setDialog] = useState({
    open: false,
    title: "",
    message: "",
    loading: false,
    action: null,
  });

  const safeStatus = report.status || "open";
  const safeEntityType = report.entityType || "unknown";
  const safeEntityLabel = report.entityLabel || report.metadata?.entityLabel || `${safeEntityType} #${String(report.entityId || "").slice(0, 8)}`;
  const safeReporterLabel = report.reporterLabel || report.reporterName || report.reporterEmail || "Unknown";

  const setStatus = async (nextStatus) => {
    setDialog({
      open: true,
      title: "Update report status",
      message: `Mark this report as ${nextStatus.replace("_", " ")}?`,
      loading: false,
      action: async () => {
        await adminService.withAdminRetry(() =>
          adminService.updateReportStatus(adminUser, report.id, nextStatus, adminNote)
        );
        toast.success(`Report marked as ${nextStatus.replace("_", " ")}`);
        onChanged?.();
      },
    });
  };

  const openEntityPath = useMemo(() => {
    if (!report?.entityId) return null;
    if (safeEntityType === "profile") return `/profile/${report.entityId}`;
    if (safeEntityType === "product") return `/product/${report.entityId}`;
    return null;
  }, [report?.entityId, safeEntityType]);

  const moderateFromReport = async (action) => {
    if (!report?.entityId) return;

    setDialog({
      open: true,
      title: "Apply moderation action",
      message: `Apply ${action} to this ${safeEntityType}?`,
      loading: false,
      action: async () => {
        const note = adminNote || `Applied from report ${report.id}`;

        if (safeEntityType === "product") {
          await adminService.withAdminRetry(() => adminService.moderateProduct(adminUser, report.entityId, action, note));
          await adminService.withAdminRetry(() => adminService.updateReportStatus(adminUser, report.id, "resolved", note));
        } else if (safeEntityType === "profile") {
          if (action === "remove") {
            await adminService.withAdminRetry(() => adminService.toggleUserBan(adminUser, report.entityId, true, note));
            await adminService.withAdminRetry(() => adminService.updateReportStatus(adminUser, report.id, "resolved", note));
          } else {
            await adminService.withAdminRetry(() => adminService.updateReportStatus(adminUser, report.id, "under_review", note));
          }
        }

        toast.success("Moderation action applied");
        onChanged?.();
      },
    });
  };

  const executeDialogAction = async () => {
    if (!dialog.action) return;
    setDialog((prev) => ({ ...prev, loading: true }));
    try {
      await dialog.action();
      setDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(err?.message || "Action failed");
      setDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 overflow-hidden">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-neutral-750 transition"
      >
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <span className="text-sm font-bold text-gray-900 dark:text-white">{safeEntityLabel}</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[safeStatus] || STATUS_STYLE.open}`}>
            {safeStatus.replace("_", " ")}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {formatTime(report.createdAt)}
          </span>
        </div>

        <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
          {report.details || "No details provided."}
        </p>

        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-3 gap-y-1">
          <span>Entity: {safeEntityType}</span>
          <span>Reported by: {safeReporterLabel}</span>
          <span>Reason: {formatReason(report.reason)}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-gray-200 dark:border-neutral-700 p-4 space-y-3">
          <textarea
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            placeholder="Add admin note (optional)"
            rows={3}
            className="w-full rounded-lg border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-green-500"
          />

          <div className="flex flex-wrap gap-2">
            {openEntityPath && (
              <Link
                to={openEntityPath}
                target="_blank"
                rel="noreferrer"
                className="px-3 py-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium hover:bg-blue-200 inline-flex items-center gap-1.5"
              >
                <ExternalLink className="w-3 h-3" />
                Open entity
              </Link>
            )}

            {safeEntityType === "product" && (
              <>
                <button
                  onClick={() => moderateFromReport("hide")}
                  className="px-3 py-1.5 rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 text-xs font-medium hover:bg-orange-200"
                >
                  Hide from profile
                </button>
                <button
                  onClick={() => moderateFromReport("remove")}
                  className="px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-200"
                >
                  Delete item
                </button>
              </>
            )}

            {safeEntityType === "profile" && (
              <button
                onClick={() => moderateFromReport("remove")}
                className="px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-200"
              >
                Ban user
              </button>
            )}

            <button
              onClick={() => setStatus("under_review")}
              className="px-3 py-1.5 rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-xs font-medium hover:bg-amber-200"
            >
              Under review
            </button>
            <button
              onClick={() => setStatus("resolved")}
              className="px-3 py-1.5 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium hover:bg-green-200"
            >
              Resolve
            </button>
            <button
              onClick={() => setStatus("rejected")}
              className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-neutral-700 text-gray-700 dark:text-gray-300 text-xs font-medium hover:bg-gray-200"
            >
              Reject
            </button>

            {isSuperAdmin && (
              <button
                onClick={() => onDelete?.(report)}
                className="ml-auto px-3 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-200 inline-flex items-center gap-1.5"
              >
                <Trash2 className="w-3 h-3" />
                Delete report
              </button>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        confirmLabel="Confirm"
        loading={dialog.loading}
        onConfirm={executeDialogAction}
        onCancel={() => {
          if (dialog.loading) return;
          setDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}

export default function AdminReports() {
  const toast = useToast();
  const { isSuperAdmin, user: adminUser } = useAdmin();
  const [status, setStatus] = useState("all");
  const [entityType, setEntityType] = useState("all");
  const [reports, setReports] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dialog, setDialog] = useState({ open: false, title: "", message: "", loading: false, action: null });

  useEffect(() => {
    const unsubscribe = adminService.listenReports(
      (rows) => setReports(rows),
      { status, entityType, limit: 100 }
    );
    return () => unsubscribe();
  }, [status, entityType]);

  const counts = useMemo(() => {
    const map = { all: reports.length };
    reports.forEach((item) => {
      const s = item.status || "open";
      map[s] = (map[s] || 0) + 1;
    });
    return map;
  }, [reports]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const allVisibleSelected = reports.length > 0 && reports.every((item) => selectedSet.has(item.id));

  useEffect(() => {
    setSelected((prev) => prev.filter((id) => reports.some((row) => row.id === id)));
  }, [reports]);

  const toggleRow = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  };

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected([]);
      return;
    }
    setSelected(reports.map((item) => item.id));
  };

  const runDialogAction = async () => {
    if (!dialog.action) return;
    setDialog((prev) => ({ ...prev, loading: true }));
    try {
      await dialog.action();
      setDialog({ open: false, title: "", message: "", loading: false, action: null });
    } catch (err) {
      toast.error(err?.message || "Action failed");
      setDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const confirmDeleteReport = (report) => {
    setDialog({
      open: true,
      title: "Delete report",
      message: "This report will be permanently removed.",
      loading: false,
      action: async () => {
          await adminService.withAdminRetry(() => adminService.deleteReport(adminUser, report.id));
        setSelected((prev) => prev.filter((id) => id !== report.id));
        toast.success("Report deleted");
      },
    });
  };

  const confirmDeleteSelected = () => {
    if (!selected.length) return;
    setDialog({
      open: true,
      title: "Delete selected reports",
      message: `Delete ${selected.length} selected reports?`,
      loading: false,
      action: async () => {
        const ids = [...selected];
          await adminService.withAdminRetry(() => adminService.deleteReportsBulk(adminUser, ids));
        setSelected([]);
        toast.success(`${ids.length} reports deleted`);
      },
    });
  };

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-20 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 h-14">
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <h1 className="text-lg font-bold text-gray-900 dark:text-white">Reports</h1>
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">{reports.length} items</span>
          </div>

          <div className="pb-3 space-y-2">
            {isSuperAdmin && (
              <div className="flex items-center justify-between">
                <button
                  onClick={toggleAll}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300 hover:text-red-600"
                >
                  {allVisibleSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                  {allVisibleSelected ? "Clear selection" : "Select all visible"}
                </button>

                {selected.length > 0 && (
                  <button
                    onClick={confirmDeleteSelected}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-xs font-medium hover:bg-red-200"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    Delete selected ({selected.length})
                  </button>
                )}
              </div>
            )}

            <div className="flex gap-1.5 overflow-x-auto">
              {STATUS_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setStatus(option.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap ${
                    status === option.id
                      ? "bg-red-600 text-white border-red-600"
                      : "border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:border-red-500"
                  }`}
                >
                  {option.label}
                  {counts[option.id] ? ` (${counts[option.id]})` : ""}
                </button>
              ))}
            </div>

            <div className="flex gap-1.5 overflow-x-auto">
              {ENTITY_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  onClick={() => setEntityType(option.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition whitespace-nowrap ${
                    entityType === option.id
                      ? "bg-neutral-900 text-white dark:bg-neutral-700 border-neutral-900 dark:border-neutral-700"
                      : "border-gray-200 dark:border-neutral-700 text-gray-600 dark:text-gray-300 hover:border-gray-400"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 pb-24 space-y-3">
        {reports.length === 0 ? (
          <div className="text-center py-16">
            <Inbox className="w-12 h-12 text-gray-300 dark:text-neutral-600 mx-auto mb-3" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No reports found</p>
          </div>
        ) : (
          reports.map((report) => (
            <div key={report.id} className="flex items-start gap-2">
              {isSuperAdmin && (
                <button
                  onClick={() => toggleRow(report.id)}
                  className="mt-4 text-gray-400 hover:text-red-600"
                  aria-label={selectedSet.has(report.id) ? "Unselect report" : "Select report"}
                >
                  {selectedSet.has(report.id) ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                </button>
              )}

              <div className="flex-1 min-w-0">
                <ReportCard
                  report={report}
                  selected={selectedSet.has(report.id)}
                  onToggleSelect={() => toggleRow(report.id)}
                  isSuperAdmin={isSuperAdmin}
                  onDelete={confirmDeleteReport}
                  adminUser={adminUser}
                />
              </div>
            </div>
          ))
        )}
      </div>

      <ConfirmDialog
        open={dialog.open}
        title={dialog.title}
        message={dialog.message}
        confirmLabel="Delete"
        destructive={true}
        loading={dialog.loading}
        onConfirm={runDialogAction}
        onCancel={() => {
          if (dialog.loading) return;
          setDialog({ open: false, title: "", message: "", loading: false, action: null });
        }}
      />
    </div>
  );
}
